import { SelectOptionType } from '@octano/global-ui';
import { ReactElement, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { getPostulantAcademicHistory } from '../../../api/academicHistoryRequests';

import DisplayError from '../../../../../../components/Info/DisplayError';
import Loading from '../../../../../../components/Info/Loading';
import { createFetchContext } from '../../../../../../components/contexts/FetchContextFactory';
import { AuthenticationError } from '../../../../../GradeEntry/Loaders/CreateEvaluationGradeEntryBaseLoader';
import { AxiosResultDefaultError } from '../../../../../../api/request';
import {
  StudentInfo,
  StudentAcademicHistory,
  StudyPlanEnrollmentsInfo,
} from '../../../../studentFileList/types';

export interface StudentAcademicHistoryLoaderProps {
  onError?: (code: string, onUnknown?: (() => void) | undefined) => void;
  children: (props: {
    student: StudentInfo;
    studyPlanEnrollments: SelectOptionType[];
    studyPlanEnrollmentsInfo: StudyPlanEnrollmentsInfo[];
    tutor: null;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  StudentAcademicHistory,
  AuthenticationError | AxiosResultDefaultError
>();

export const StudentAcademicHistoryLoaderConsumer = FetchConsumer;

export function useStudentAcademicHistory() {
  return useFetch();
}

export default function StudentAcademicHistoryLoader({
  onError,
  children,
}: StudentAcademicHistoryLoaderProps) {
  const { studentId } = useParams<{ studentId: string }>();
  const request = useCallback(() => {
    return getPostulantAcademicHistory(studentId);
  }, [studentId]);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            onError && onError(error.code);
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return <Loading insideCard />;
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children({
            student: data.student,
            studyPlanEnrollments: data.studyPlanEnrollments.map(
              (studyPlanEnrollment: StudyPlanEnrollmentsInfo) => ({
                label: studyPlanEnrollment.name.toUpperCase(),
                value: studyPlanEnrollment.studyPlanEnrollmentId,
              }),
            ),
            studyPlanEnrollmentsInfo: data.studyPlanEnrollments,
            tutor: null,
          });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
